import { useEffect, useState } from 'react';

// MUI (Mantis)
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from '@mui/material';

import api from '../../api/axios';

// DD-MM-YYYY HH:mm
const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);

  const pad = (n) => String(n).padStart(2, '0');
  const DD = pad(d.getDate());
  const MM = pad(d.getMonth() + 1);
  const YYYY = d.getFullYear();
  const HH = pad(d.getHours());
  const mm = pad(d.getMinutes());

  return `${DD}-${MM}-${YYYY} ${HH}:${mm}`;
};

const eventBadgeStyle = (type) => {
  const t = String(type || '').toLowerCase();

  const isStarted = t.includes('started');
  const isCompleted = t.includes('completed');
  const isSent = t.includes('sent');
  const isFailed = t.includes('failed') || t.includes('error');

  const bg = isCompleted
    ? 'rgba(34,197,94,0.15)'
    : isFailed
      ? 'rgba(239,68,68,0.15)'
      : isSent
        ? 'rgba(59,130,246,0.15)'
        : isStarted
          ? 'rgba(245,158,11,0.15)'
          : 'rgba(148,163,184,0.15)';

  const bd = isCompleted
    ? 'rgba(34,197,94,0.35)'
    : isFailed
      ? 'rgba(239,68,68,0.35)'
      : isSent
        ? 'rgba(59,130,246,0.35)'
        : isStarted
          ? 'rgba(245,158,11,0.35)'
          : 'rgba(148,163,184,0.35)';

  const fg = isCompleted ? '#22c55e' : isFailed ? '#ef4444' : isSent ? '#60a5fa' : isStarted ? '#f59e0b' : '#94a3b8';

  return { background: bg, borderColor: bd, color: fg };
};

// ================== NUMBER FORMAT ==================
const extractDigitsFromWaId = (wa) => {
  if (!wa) return '';
  return String(wa).split('@')[0].replace(/\D/g, '');
};

const group = (s, groups) => {
  let i = 0;
  const out = [];
  for (const g of groups) {
    if (i >= s.length) break;
    out.push(s.slice(i, i + g));
    i += g;
  }
  if (i < s.length) out.push(s.slice(i));
  return out.join(' ');
};

const formatE164Simple = (digits) => {
  const d = String(digits || '').replace(/\D/g, '');
  if (!d) return '-';

  if (d.startsWith('62')) return `+62 ${group(d.slice(2), [3, 4, 4])}`.trim();
  if (d.startsWith('1')) return `+1 ${group(d.slice(1), [3, 3, 4])}`.trim();
  if (d.startsWith('63')) return `+63 ${group(d.slice(2), [3, 3, 4])}`.trim();

  const ccLen = d.length >= 11 ? 2 : 1;
  const cc = d.slice(0, ccLen);
  const rest = d.slice(ccLen);
  return `+${cc} ${group(rest, [3, 4, 4])}`.trim();
};

const formatWaNumber = (waIdOrNumber) => {
  const digits = extractDigitsFromWaId(waIdOrNumber);
  return formatE164Simple(digits);
};
// ===================================================

const pickUserId = (u) => u?.id ?? null;
const pickUsername = (u) => u?.username ?? null;

const mapEventRow = (e) => ({
  ID: e?.ID ?? e?.id ?? null,
  JobID: e?.JobID ?? e?.jobId ?? '',
  SessionName: e?.SessionName ?? e?.sessionName ?? '',
  Number: e?.Number ?? e?.number ?? '',
  EventType: e?.EventType ?? e?.eventType ?? '',
  Reason: e?.Reason ?? e?.reason ?? '',
  Source: e?.Source ?? e?.source ?? '',
  CreatedBy: e?.CreatedBy ?? e?.createdBy ?? null,
  CreatedAt: e?.CreatedAt ?? e?.createdAt ?? ''
});

function Log() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // server pagination (1-based)
  const [perPage, setPerPage] = useState(50);
  const [pageIndex, setPageIndex] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // filter by sessionName (server-side)
  const [sessionName, setSessionName] = useState('');

  // createdBy -> username map
  const [userMap, setUserMap] = useState({});
  const [usersLoaded, setUsersLoaded] = useState(false);

  // ===== job detail modal =====
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailData, setDetailData] = useState(null);
  const [detailMeta, setDetailMeta] = useState({ sessionName: '', jobId: '' });

  const buildJobDetailPath = (session, jobId) => {
    const s = encodeURIComponent(String(session || '').trim());
    const j = encodeURIComponent(String(jobId || '').trim());
    return `/api/sessions/${s}/messages/${j}`;
  };

  const openJobDetail = async (row) => {
    const jobId = row?.JobID;
    const sName = row?.SessionName;
    if (!jobId) return;

    setDetailMeta({ sessionName: sName || '-', jobId: String(jobId) });
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    setDetailData(null);

    try {
      const res = await api.get(buildJobDetailPath(sName, jobId));
      setDetailData(res?.data || null);
    } catch (e) {
      console.error('fetch job detail error:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to load job detail';
      setDetailError(String(msg));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeJobDetail = () => {
    setDetailOpen(false);
    setDetailLoading(false);
    setDetailError('');
    setDetailData(null);
    setDetailMeta({ sessionName: '', jobId: '' });
  };

  // ===========================
  // ✅ YANG DIGANTI: BUILD PATH
  // ===========================
  const buildPath = () => {
    const q = sessionName.trim();
    const qs = new URLSearchParams();
    if (q) qs.set('sessionName', q);

    // endpoint BARU: /api/session-events/{limit}/{page}?sessionName=
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return `/api/session-events/${perPage}/${pageIndex}${suffix}`;
  };

  // =========================================
  // ✅ YANG DIGANTI: FETCH EVENTS PAKE buildPath
  // =========================================
  const fetchEvents = async () => {
    setLoading(true);
    try {
      const url = buildPath();
      const res = await api.get(url);

      const payload = res?.data || {};
      const list = Array.isArray(payload.events) ? payload.events : [];
      setRows(list.map(mapEventRow));

      const limit = Number(payload.limit) || perPage;
      const total = Number(payload.total) || 0;

      setTotalCount(total);
      setTotalPages(Math.max(1, Math.ceil(total / Math.max(1, limit))));
    } catch (e) {
      console.error('fetch session-events error:', e);
      setRows([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // NOTE: ini opsional, tapi bikin aman kalau totalPages mengecil setelah filter
  useEffect(() => {
    setPageIndex((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  // ===========================
  // users (tetap, tapi HAPUS AUTH kalau gak ada)
  // ===========================
  const fetchUsers = async () => {
    try {
      // kalau axios instance kamu sudah handle auth via interceptor:
      const res = await api.get('/api/users/all/100/1');
      const payload = res?.data || {};
      const list = Array.isArray(payload.users) ? payload.users : [];

      const map = {};
      list.forEach((u) => {
        const id = pickUserId(u);
        const name = pickUsername(u);
        if (id == null || !name) return;
        map[Number(id)] = String(name);
      });

      setUserMap(map);
      setUsersLoaded(true);
    } catch {
      setUserMap({});
      setUsersLoaded(true);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ✅ tetep: refetch kalau page/perPage/filter berubah
  useEffect(() => {
    fetchEvents();
  }, [perPage, pageIndex, sessionName]);

  const createdByName = (createdBy) => {
    if (createdBy === undefined || createdBy === null) return '-';
    const id = Number(createdBy);
    if (id === 0) return 'system';
    return userMap[id] || `user#${id}`;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardHeader
          title="Session Events"
          action={
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <TextField
                size="small"
                value={sessionName}
                onChange={(e) => {
                  setSessionName(e.target.value);
                  setPageIndex(1); // ✅ tetep: reset ke page 1 kalau filter berubah
                }}
                placeholder="sessionName..."
                sx={{ width: { xs: 220, md: 260 } }}
              />
            </Stack>
          }
        />

        <CardContent>
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  {['ID', 'JOB ID', 'SESSION', 'NUMBER', 'EVENT', 'REASON', 'CREATED AT'].map((h) => (
                    <Box
                      key={h}
                      component="th"
                      sx={{
                        p: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        textAlign: 'left',
                        whiteSpace: 'nowrap',
                        fontWeight: 600
                      }}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box component="tbody">
                {rows.length === 0 ? (
                  <Box component="tr">
                    <Box component="td" colSpan={7} sx={{ p: 1, opacity: 0.8 }}>
                      {loading ? 'Loading...' : sessionName.trim() ? 'No matching events' : 'No events'}
                    </Box>
                  </Box>
                ) : (
                  rows.map((d) => {
                    const st = eventBadgeStyle(d?.EventType);
                    const prettyNumber = formatWaNumber(d.Number);
                    const byName = createdByName(d.CreatedBy);

                    return (
                      <Box component="tr" key={d.ID}>
                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {d.ID}
                          </Typography>
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          {d.JobID ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography
                                variant="body2"
                                onClick={() => openJobDetail(d)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') openJobDetail(d);
                                }}
                                sx={{
                                  fontFamily: 'monospace',
                                  opacity: 0.95,
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  textUnderlineOffset: '3px'
                                }}
                                title="Klik untuk lihat detail pengiriman"
                              >
                                {d.JobID}
                              </Typography>

                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 999,
                                  border: '1px solid',
                                  borderColor: 'rgba(59,130,246,0.35)',
                                  background: 'rgba(59,130,246,0.12)',
                                  color: '#60a5fa',
                                  fontSize: 11,
                                  fontWeight: 800,
                                  letterSpacing: 0.2
                                }}
                              >
                                DETAIL
                              </Box>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', opacity: 0.6 }}>
                                -
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.5 }}>
                                no job
                              </Typography>
                            </Stack>
                          )}
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {d.SessionName || '-'}
                          </Typography>
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', opacity: 0.9 }}>
                            {prettyNumber}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.6, fontFamily: 'monospace' }}>
                            raw: {d.Number || '-'}
                          </Typography>
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1.25,
                              py: 0.5,
                              borderRadius: 999,
                              border: '1px solid',
                              fontSize: 12,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              ...st
                            }}
                          >
                            {d.EventType || '-'}
                          </Box>
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          {d.Reason ? (
                            <Typography variant="body2">{d.Reason}</Typography>
                          ) : (
                            <Typography variant="body2" sx={{ opacity: 0.6 }}>
                              -
                            </Typography>
                          )}
                          {d.Source ? (
                            <Typography variant="caption" sx={{ opacity: 0.65 }}>
                              source: <b>{d.Source}</b>
                            </Typography>
                          ) : null}
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {fmtDate(d.CreatedAt)}
                          </Typography>

                          <Typography variant="caption" sx={{ opacity: 0.65 }}>
                            createdBy: <b>{byName}</b>
                            {!usersLoaded ? ' (loading users...)' : ''}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Total: {totalCount} • Page {pageIndex} / {totalPages}
              {sessionName.trim() ? ` • sessionName=${sessionName.trim()}` : ''}
            </Typography>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                component="button"
                type="button"
                onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
                disabled={pageIndex <= 1 || totalCount === 0}
                style={{
                  border: '1px solid rgba(255,255,255,0.18)',
                  background: 'transparent',
                  color: 'inherit',
                  borderRadius: 10,
                  padding: '6px 10px',
                  cursor: pageIndex <= 1 || totalCount === 0 ? 'not-allowed' : 'pointer',
                  opacity: pageIndex <= 1 || totalCount === 0 ? 0.5 : 1
                }}
              >
                Prev
              </Box>

              <Box
                component="button"
                type="button"
                onClick={() => setPageIndex((p) => Math.min(totalPages, p + 1))}
                disabled={pageIndex >= totalPages || totalCount === 0}
                style={{
                  border: '1px solid rgba(255,255,255,0.18)',
                  background: 'transparent',
                  color: 'inherit',
                  borderRadius: 10,
                  padding: '6px 10px',
                  cursor: pageIndex >= totalPages || totalCount === 0 ? 'not-allowed' : 'pointer',
                  opacity: pageIndex >= totalPages || totalCount === 0 ? 0.5 : 1
                }}
              >
                Next
              </Box>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPageIndex(1); // ✅ reset ke page 1 kalau perPage berubah
                  }}
                >
                  {[10, 20, 50, 100, 200].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} / page
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          {/* ===== modal detail: tidak diubah ===== */}
          <Dialog open={detailOpen} onClose={closeJobDetail} fullWidth maxWidth="md">
            <DialogTitle sx={{ pb: 1 }}>
              <Stack spacing={0.5}>
                <Typography sx={{ fontWeight: 700 }}>Job Messages</Typography>
                <Typography variant="caption" sx={{ opacity: 0.75, fontFamily: 'monospace' }}>
                  session: {detailMeta.sessionName} • jobId: {detailMeta.jobId}
                </Typography>
              </Stack>
            </DialogTitle>

            <DialogContent dividers>
              {detailLoading ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Loading detail...
                  </Typography>
                </Stack>
              ) : detailError ? (
                <Typography variant="body2" sx={{ color: '#ef4444' }}>
                  {detailError}
                </Typography>
              ) : !detailData ? (
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  No data
                </Typography>
              ) : (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Status:
                        <Box
                          component="span"
                          sx={{
                            ml: 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.25,
                            py: 0.35,
                            borderRadius: 999,
                            border: '1px solid',
                            fontSize: 12,
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            ...eventBadgeStyle(detailData?.status)
                          }}
                        >
                          {detailData?.status || '-'}
                        </Box>
                      </Typography>

                      <Typography variant="caption" sx={{ opacity: 0.7, fontFamily: 'monospace' }}>
                        createdAt: {fmtDate(detailData?.createdAt)} • startedAt: {fmtDate(detailData?.startedAt)} • finishedAt:{' '}
                        {fmtDate(detailData?.finishedAt)}
                      </Typography>
                    </Stack>

                    <Stack spacing={0.25} sx={{ minWidth: 240 }}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        total: <b>{detailData?.total ?? '-'}</b> • sent: <b>{detailData?.sent ?? '-'}</b> • failed:{' '}
                        <b>{detailData?.failed ?? '-'}</b>
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.65 }}>
                        intervalSeconds: <b>{detailData?.intervalSeconds ?? '-'}</b>
                      </Typography>
                    </Stack>
                  </Stack>

                  <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                      <Box component="thead">
                        <Box component="tr">
                          {['TO', 'STATUS'].map((h) => (
                            <Box
                              key={h}
                              component="th"
                              sx={{
                                p: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                                fontWeight: 700
                              }}
                            >
                              {h}
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      <Box component="tbody">
                        {Array.isArray(detailData?.results) && detailData.results.length > 0 ? (
                          detailData.results.map((r, idx) => (
                            <Box component="tr" key={`${r?.to || 'to'}-${idx}`}>
                              <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {formatWaNumber(r?.to)}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.6, fontFamily: 'monospace' }}>
                                  raw: {r?.to || '-'}
                                </Typography>
                              </Box>

                              <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 1.25,
                                    py: 0.4,
                                    borderRadius: 999,
                                    border: '1px solid',
                                    fontSize: 12,
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                    ...eventBadgeStyle(r?.status)
                                  }}
                                >
                                  {r?.status || '-'}
                                </Box>
                                {r?.messageId ? (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.65, fontFamily: 'monospace' }}>
                                    messageId: {r.messageId}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Box>
                          ))
                        ) : (
                          <Box component="tr">
                            <Box component="td" colSpan={2} sx={{ p: 1, opacity: 0.75 }}>
                              No results
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {detailData?.errors ? (
                    <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 700 }}>
                        Errors
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', opacity: 0.85 }}>
                        {typeof detailData.errors === 'string' ? detailData.errors : JSON.stringify(detailData.errors, null, 2)}
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={closeJobDetail} variant="outlined">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Log;
